import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/en-au';
import React from 'react';
import styled from 'styled-components';

import useScreenQuery from '../../../hooks/useScreenQuery';
import { formatCurrency } from '../../../utils/formatCurrency';
import LimitChangeConfirmModal from './LimitChangeConfirmModal';

dayjs.extend(localizedFormat);

dayjs.locale('en-au');

const ActiveLimits = ({ activeLimits, pendingLimits, limitName, onRemove, isLoading }: ActiveLimitsProps) => {
	const [showConfirmModal, setShowConfirmModal] = React.useState(false);
	const { isSmall } = useScreenQuery();
	const currentDurationRef = React.useRef<number>();

	const handleRemove = () => {
		const limitToRemove = activeLimits.find((activeLimit) => activeLimit.id === currentDurationRef.current);

		if (limitToRemove) {
			onRemove(limitToRemove.type.id, limitToRemove.duration.id);
		}

		currentDurationRef.current = undefined;
	};

	React.useEffect(() => {
		if (!isLoading) setShowConfirmModal(false);
	}, [isLoading]);

	return (
		<Wrapper>
			<h4>Current {limitName} limit</h4>
			<ActiveLimitsWrapper isSmall={isSmall}>
				{activeLimits.map((activeLimit) => (
					<div key={activeLimit.id}>
						{activeLimit.duration && (
							<p>{activeLimit.limit ? activeLimit.duration.name : <strong>{activeLimit.duration.name}</strong>}</p>
						)}
						{activeLimit.limit && (
							<p>
								<>
									<strong>{formatCurrency(activeLimit.limit)}</strong> (
									{formatCurrency(Math.max(activeLimit.limit - activeLimit.used, 0))} left)
								</>
							</p>
						)}
						<p>Set: {dayjs(activeLimit.created_at).format('L')}</p>
						<p>
							Expires: {dayjs(activeLimit.end_date).format('L')} at {dayjs(activeLimit.end_date).format('HH:mm')}
						</p>
						<button
							onClick={() => {
								currentDurationRef.current = activeLimit.id;
								setShowConfirmModal(true);
							}}
						>
							Remove
						</button>
					</div>
				))}
			</ActiveLimitsWrapper>
			{!!pendingLimits?.length && (
				<>
					<h4>Limit change request</h4>
					<ActiveLimitsWrapper isSmall={isSmall}>
						{pendingLimits.map((pendingLimit) => (
							<div key={pendingLimit.id}>
								{pendingLimit.duration && (
									<p>
										{pendingLimit.limit ? pendingLimit.duration.name : <strong>{pendingLimit.duration.name}</strong>}
									</p>
								)}
								{pendingLimit.limit && (
									<p>
										<strong>{formatCurrency(pendingLimit.limit)}</strong>
									</p>
								)}
								{pendingLimit.limit === null && <p>(limit will be removed)</p>}
								<p>Requested on: {dayjs(pendingLimit.created_at).format('L')}</p>
								<p>
									Will be {pendingLimit.limit === null ? 'removed' : 'active'} on:{' '}
									{dayjs(pendingLimit.activate_at).format('L')} at {dayjs(pendingLimit.activate_at).format('HH:mm')}
								</p>
							</div>
						))}
					</ActiveLimitsWrapper>
				</>
			)}

			<LimitChangeConfirmModal
				onCancel={() => setShowConfirmModal(false)}
				onConfirm={handleRemove}
				message="remove"
				isLoading={isLoading}
				isVisible={showConfirmModal}
			/>
		</Wrapper>
	);
};

export default ActiveLimits;

interface ActiveLimitsProps {
	activeLimits: Array<PortalUserLimit>;
	pendingLimits?: Array<PortalUserLimitRequest>;
	limitName: string;
	onRemove: (typeId: number, durationId: number) => void;
	isLoading?: boolean;
}

const Wrapper = styled.div`
	background-color: #eee;
	color: black;
	margin-bottom: 1rem;

	h4 {
		text-align: center;
		font-weight: 600;
		padding: 0.25rem;
	}
`;

const ActiveLimitsWrapper = styled.div<{ isSmall: boolean }>`
	background-color: #f7f7f7;
	display: flex;
	flex-direction: ${({ isSmall }) => (isSmall ? 'column' : 'row')};
	justify-content: space-evenly;

	> div {
		flex-grow: 1;
		text-align: center;
		padding: 0.5rem 0;
		margin: ${({ isSmall }) => (isSmall ? '0' : '0.5')}rem 0;

		&:not(:first-child) {
			border-left: ${({ isSmall }) => (isSmall ? '0' : '1')}px solid #e1e1e1;
			border-top: ${({ isSmall }) => (isSmall ? '1' : '0')}px solid #e1e1e1;
		}

		p,
		button {
			color: black;
			font-size: 0.9rem;
			margin: 0;

			strong {
				font-size: 1.1rem;
			}
		}

		button {
			text-decoration: underline;
			transition: 200ms;
			padding: 0.2rem 0.8rem;

			&:hover {
				opacity: 0.7;
			}
		}
	}
`;
